import { useTheme } from "@mui/material";
import { NodeProps } from "reactflow";
import StandardNode from "components/Flow/Nodes/StandardNode";
import StandardNodeHeader from "components/Flow/Nodes/StandardNode/StandardNodeHeader";
import StandardNodeFunctionality from "components/Flow/Nodes/StandardNode/StandardNodeFunctionality";
import StandardNodeFooter from "components/Flow/Nodes/StandardNode/StandardNodeFooter";
import { functionalityValuesAPI, campaignNodeAPI } from "api/Campaigns/CampaignAPI";

export interface CampaignPathwayNodeData {
  name: string;
  category?: string;
  type?: string;
  colorPrimary: string;

  linkedNode?: boolean;
  baseLink?: string;
  linkedId?: string | number;

  functionalities?: any[];
  people?: any[];
  peopleCount?: number;
  addresses?: any[];
  addressesCount?: number;
  itemTypes?: "People" | "Addresses" | "Both";

  updateNode: (id: string, field: string, value: any) => void;
  updateNodeFunctionality: (
    nodeId: string,
    functionalityId: number,
    field: string,
    value: any,
    valueId?: number | null
  ) => void;
}

const CampaignPathwayNode = ({
  id,
  data,
  selected,

}: NodeProps<CampaignPathwayNodeData>) => {
  const theme = useTheme();

  /**
   * Wrapper around your update API.
   * Adjust the body to match your actual backend endpoint.
   *
   * @param id        – e.g. campaign id / node id (whatever your API expects)
   * @param functionalityId – the specific functionality being changed
   * @param field           – backend field name, e.g. "float_value"
   * @param value           – new value (number / boolean / string)
   */
  const handleFunctionalityUpdateField = async (
    functionalityId: number,
    valueId: number | null,      // may be null
    field: string,
    value: any
  ) => {
    let newValueId = valueId;

    if (!valueId) {
      // Create the missing functionality_value record
      const created = await functionalityValuesAPI.create({
        node: Number(id),                    // ReactFlow node is the backend node ID
        functionality_type: Number(functionalityId),
        value: value,
      });

      newValueId = created.id;
    } else {
      // Update existing functionality_value
      await functionalityValuesAPI.updateField(Number(valueId), field, value);
    }

    // Update UI immediately
    data.updateNodeFunctionality(
      id,
      functionalityId,
      field,
      value,
      newValueId    // pass the new id if it was created
    );
  };



  const handleNodeUpdateField = async (
    nodeId: number | string,
    field: string,
    value: any
  ) => {
    await campaignNodeAPI.updateField(Number(nodeId), field, value);

    // FIXME — this line was wrong
    data.updateNode(String(id), field, value);
  };
  


  return (
    <StandardNode
      selected={selected}
      borderColor={data.colorPrimary}
      backgroundColor={theme.custom.nodes.background.main.dark}
      header={
        <StandardNodeHeader
          title={data.name}
          subtitle={data.category}
          secondaryText={data.type}
          backgroundColor={data.colorPrimary}
          textColor={theme.custom.nodes.text.primary.dark}
          secondaryTextColor={theme.custom.nodes.text.secondary.dark}
          linkUrl={
            data.linkedNode && data.baseLink && data.linkedId
              ? `${data.baseLink}${data.linkedId}`
              : undefined
          }
          apiNodeUpdateFieldFn={handleNodeUpdateField}
          nodeId={id}
        />
      }
      functionalities={
        <StandardNodeFunctionality
          functionalities={data.functionalities || []}
          apiFunctionalityUpdateFieldFn={handleFunctionalityUpdateField}
          nodeId={Number(id)}
          nodeAPI={campaignNodeAPI}
          currentPeople={data.people ?? []}
          currentAddresses={data.addresses ?? []}
        />
      }
      footer={
        (() => {
          const showPeople =
            data.itemTypes === "People" || data.itemTypes === "Both";

          const showAddresses =
            data.itemTypes === "Addresses" || data.itemTypes === "Both";

          return (
            <StandardNodeFooter
              centerSection={{
                value: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                    }}
                  >
                    {showPeople && (
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontWeight: 600 }}>
                          {data.peopleCount ?? 0}
                        </div>
                        <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>
                          People
                        </div>
                      </div>
                    )}

                    {showAddresses && (
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontWeight: 600 }}>
                          {data.addressesCount ?? 0}
                        </div>
                        <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>
                          Addresses
                        </div>
                      </div>
                    )}
                  </div>
                ),
              }}
            />
          );
        })()
      }

    />
  );
};

export default CampaignPathwayNode;
